import { useState } from "react";
import moment, { Moment } from "moment";
import ezStore from "ezStore";
import { Box, IconButton, Tooltip } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import DatePicker from "components/cck/DatePicker";
import { getPluginSettingValue } from "imports/Utils";
import { formatDate } from "utils/DateUtils";
import { PeriodWindow } from "./PeriodField";

const getMonthStartDay = () =>
	Number(getPluginSettingValue(ezStore.getState().session, "fiscalMonthStartDay")) || 1;

const clampDayOfMonth = (date: Moment, day: number) =>
	date.clone().date(Math.min(Math.max(day, 1), date.daysInMonth())).startOf("day");

const monthWindow = (from: Moment, monthStartDay: number): PeriodWindow =>
	({ from, to: clampDayOfMonth(from.clone().add(1, "month"), monthStartDay).subtract(1, "millisecond") });

const getNamedMonthWindow = (date: Date | number | Moment): PeriodWindow => {
	const monthStartDay = getMonthStartDay();

	return monthWindow(clampDayOfMonth(moment(date), monthStartDay), monthStartDay);
};

export const getMonthWindow = (date?: Date | number | Moment | null): PeriodWindow => {
	const monthStartDay = getMonthStartDay();
	const target = moment(date ?? undefined);
	const start = clampDayOfMonth(target, monthStartDay);

	return target.isSameOrAfter(start)
		? monthWindow(start, monthStartDay)
		: monthWindow(clampDayOfMonth(target.clone().subtract(1, "month"), monthStartDay), monthStartDay);
};

const MonthField = ({
	onChange,
	defaultStart,
	simple,
	disabled,
}: {
	onChange: (window: PeriodWindow) => void;
	defaultStart?: Date | number | Moment;
	simple?: boolean;
	disabled?: boolean;
}) => {

	const [ window, setWindow ] = useState(() => getMonthWindow(defaultStart));

	const handleChange = (date: Date | number | Moment) => {
		const newWindow = getNamedMonthWindow(date);

		setWindow(newWindow);
		onChange(newWindow);
	};

	const picker = (
		<DatePicker
			showMonthYearPicker
			dateFormat="MMMM yyyy"
			disabled={disabled}
			isClearable={false}
			value={window.from.valueOf()}
			onChange={(date) => date && handleChange(date)} />
	);

	if (simple) {
		return picker;
	}

	return (
		<Tooltip title={getMonthStartDay() !== 1 ? `${formatDate(window.from, "DD/MM/YYYY")} - ${formatDate(window.to, "DD/MM/YYYY")}` : ""}>
			<Box sx={{ alignItems: "center", display: "flex", gap: 0.5 }}>
				<IconButton
					disabled={disabled}
					onClick={() => handleChange(window.from.clone().subtract(1, "month"))}
					size="small">
					<KeyboardArrowLeftIcon />
				</IconButton>

				<Box sx={{ width: "150px" }}>{picker}</Box>

				<IconButton
					disabled={disabled}
					onClick={() => handleChange(window.from.clone().add(1, "month"))}
					size="small">
					<KeyboardArrowRightIcon />
				</IconButton>
			</Box>
		</Tooltip>
	);
};

export default MonthField;
